import React, { useEffect, useState } from "react";
import { Card, Typography, Empty, Spin, List, Tag, Avatar } from "antd";
import UserServ from "../service/UserServ";
import CreditServ from "../service/CreditServ";
import Breadcrumbs from "../component/Breadcrumbs";
import { IconGift, IconArrowUpRight, IconArrowDownLeft } from '@tabler/icons-react';
import { formatDate } from "../util/dateUtils";

const { Title, Text } = Typography;

const BuyerRewardPoints = () => {
    const [stats, setStats] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchData = async (showLoading = true) => {
        try {
            if (showLoading && transactions.length === 0) setLoading(true);
            const cachedStats = localStorage.getItem('cached_buyer_reward_stats');
            const cachedTrans = localStorage.getItem('cached_buyer_reward_trans');
            if (cachedStats && cachedTrans && showLoading) {
                try {
                    setStats(JSON.parse(cachedStats));
                    setTransactions(JSON.parse(cachedTrans));
                    setLoading(false); // Stop skeleton immediately
                } catch(e) {}
            }

            const [statsRes, transRes] = await Promise.all([
                UserServ.getBuyerStats(),
                CreditServ.getTransactions({ limit: 10 })
            ]);

            if (statsRes.data) {
                setStats(statsRes.data);
                localStorage.setItem('cached_buyer_reward_stats', JSON.stringify(statsRes.data));
            }

            // Handle transaction response structure (check if it's array or paginated object)
            let transData = [];
            if (transRes.data && Array.isArray(transRes.data)) {
                transData = transRes.data;
            } else if (transRes.transactions && Array.isArray(transRes.transactions)) {
                transData = transRes.transactions;
            } else if (Array.isArray(transRes)) {
                transData = transRes;
            }
            setTransactions(transData);
            localStorage.setItem('cached_buyer_reward_trans', JSON.stringify(transData));
        } catch (error) {
            console.error("Failed to load rewards data", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(true);
    }, []);

    const renderTransactionIcon = (type) => {
        if (type === 'earned' || type === 'bonus' || type === 'referral' || type === 'refund') {
            return <Avatar style={{ backgroundColor: '#f6ffed', color: '#52c41a' }} icon={<IconArrowDownLeft size={18} />} />;
        }
        return <Avatar style={{ backgroundColor: '#fff1f0', color: '#f5222d' }} icon={<IconArrowUpRight size={18} />} />;
    };

    return (
        <div className="sd-container pcu-max-w-1200 pcu-margin-x-auto pcu-padding-20">
            <div className="pcu-margin-bottom-16">
                <Breadcrumbs />
            </div>

            <div className="sd-header" style={{ marginBottom: '24px', background: 'transparent', padding: 0 }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 800, color: '#1a1d2e', letterSpacing: '-0.5px', display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #fdf4ff, #f3e8ff)', border: '1px solid rgba(139,92,246,0.25)' }}>
                            <IconGift size={20} color="#7c3aed" stroke={1.8} />
                        </span>
                        Reward Points
                    </h1>
                </div>
            </div>

            <div className="pcu-margin-top-24">
                {loading ? (
                    <div className="pcu-flex-center pcu-padding-y-100 pcu-flex-column pcu-gap-16">
                        <Spin size="large" />
                        <Text type="secondary" className="pcu-font-14">Refining your rewards...</Text>
                    </div>
                ) : (
                    <div className="pcu-flex-column pcu-gap-28">
                        {/* Professional Balance Card - Compact & Aligned */}
                        <Card
                            className="pcu-radius-20 pcu-border-none pcu-overflow-hidden pcu-shadow-md"
                            bodyStyle={{
                                padding: '32px',
                                background: 'linear-gradient(135deg, rgb(196 195 214) 0%, #56a7ea 40%, rgb(192, 38, 211) 100%)',
                                position: 'relative'
                            }}
                        >
                            {/* Subtler Decorative Elements */}
                            <div style={{
                                position: 'absolute',
                                top: '-40px',
                                right: '-40px',
                                width: '200px',
                                height: '200px',
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, rgba(255,165,0,0.4) 0%, rgba(236,72,153,0.4) 100%)',
                                filter: 'blur(40px)',
                                zIndex: 0
                            }}></div>
                            <div style={{
                                position: 'absolute',
                                bottom: '-40px',
                                left: '-40px',
                                width: '150px',
                                height: '150px',
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, rgba(79,70,229,0.3) 0%, rgba(147,51,234,0.3) 100%)',
                                filter: 'blur(35px)',
                                zIndex: 0
                            }}></div>

                            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '20px' }} className="pcu-relative pcu-z-10">
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '12px' }}>
                                    <Text className="pcu-font-12 pcu-font-800 pcu-color-white-90" style={{ letterSpacing: '1px' }}>Available Rewards</Text>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <Title level={1} className="pcu-color-white pcu-font-800" style={{ margin: 0, fontSize: '48px', lineHeight: 1, letterSpacing: '-0.5px' }}>
                                            {stats?.credits?.toLocaleString() || 0}
                                        </Title>
                                        <div className="pcu-bg-white pcu-color-primary pcu-font-800" style={{ padding: '4px 12px', borderRadius: '8px', fontSize: '12px' }}>
                                            Points
                                        </div>
                                    </div>
                                    <Text className="pcu-color-white-80 pcu-font-15 pcu-font-500">Your Current Balance</Text>
                                </div>
                                <div className="pcu-bg-white-20" style={{ width: '80px', height: '80px', borderRadius: '20px', backdropFilter: 'blur(8px)', border: '1px solid rgba(255, 255, 255, 0.25)', display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0 }}>
                                    <IconGift size={40} color="white" stroke={1.5} />
                                </div>
                            </div>

                            <div className="pcu-relative pcu-z-10" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.15)', marginTop: '28px', paddingTop: '16px' }}>
                                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px' }}>
                                        <div className="pcu-bg-white-20" style={{ padding: '2px 8px', borderRadius: '4px' }}>
                                            <Text className="pcu-color-white pcu-font-10 pcu-font-800">Active</Text>
                                        </div>
                                        <Text style={{ marginTop: "10px" }} className="pcu-color-white-80 pcu-font-13">Rewards are updated instantly after purchase</Text>
                                    </div>
                                    <Text className="pcu-color-white pcu-font-13 pcu-font-700 cursor-pointer hover:pcu-underline">Learn More →</Text>
                                </div>
                            </div>
                        </Card>

                        {/* Transaction History Section */}
                        <div className="pcu-flex-column pcu-gap-20">
                            <div className="pcu-flex pcu-justify-between pcu-flex-center-y pcu-padding-x-4">
                                <div className="pcu-flex-center-y pcu-gap-12">
                                    <Title level={4} className="pcu-margin-0 pcu-font-800 pcu-text-22">Transaction History</Title>
                                    <Tag className="pcu-border-none pcu-bg-orange-50 pcu-color-primary pcu-font-11 pcu-font-800 pcu-radius-6 pcu-padding-x-8">{transactions.length} ACTIVITIES</Tag>
                                </div>
                                <Text className="pcu-font-13 pcu-font-700 cursor-pointer pcu-color-primary">Export History</Text>
                            </div>

                            <div className="pcu-flex-column pcu-gap-14">
                                {transactions.length > 0 ? (
                                    transactions.map((item, idx) => (
                                        <div key={idx} className="pcu-bg-white pcu-radius-20 pcu-padding-20 pcu-shadow-sm pcu-border-light pcu-flex pcu-justify-between pcu-flex-center-y pcu-transition-all hover:pcu-shadow-md">
                                            <div className="pcu-flex pcu-gap-20 pcu-flex-center-y">
                                                <div className={`pcu-padding-14 pcu-radius-16 pcu-flex-center ${(item.type === 'earned' || item.type === 'bonus' || item.type === 'referral') ? 'pcu-bg-success-10' : 'pcu-bg-error-10'}`}>
                                                    {(item.type === 'earned' || item.type === 'bonus' || item.type === 'referral') ?
                                                        <IconArrowDownLeft size={24} color="#10b981" stroke={2.5} /> :
                                                        <IconArrowUpRight size={24} color="#ef4444" stroke={2.5} />
                                                    }
                                                </div>
                                                <div className="pcu-flex-column pcu-gap-4">
                                                    <Text strong className="pcu-font-16 pcu-color-gray-900">{item.description || "Point Transaction"}</Text>
                                                    <div className="pcu-flex-center-y pcu-gap-8">
                                                        <Text className="pcu-font-12 pcu-color-gray-500">{formatDate(item.createdAt)}</Text>
                                                        <span className="pcu-w-4 pcu-h-4 pcu-radius-50 pcu-bg-gray-300"></span>
                                                        <Text className="pcu-font-11 pcu-color-gray-400 pcu-font-700">REF: {item.referenceId?.slice(-6).toUpperCase() || 'TRX-DEFAULT'}</Text>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="pcu-text-right pcu-flex-column pcu-gap-4">
                                                <Title level={4} className={`pcu-margin-0 pcu-font-800 pcu-text-20 ${(item.type === 'earned' || item.type === 'bonus' || item.type === 'referral') ? 'pcu-color-success' : 'pcu-color-error'}`}>
                                                    {(item.type === 'earned' || item.type === 'bonus' || item.type === 'referral') ? '+' : '-'}{item.amount}
                                                </Title>
                                                <div className="pcu-flex pcu-justify-end">
                                                    <div className={`pcu-padding-x-8 pcu-padding-y-2 pcu-radius-6 pcu-font-10 pcu-font-800 pcu-uppercase pcu-letter-spacing-1 ${(item.type === 'earned' || item.type === 'bonus') ? 'pcu-bg-success-10 pcu-color-success' : item.type === 'referral' ? 'pcu-bg-primary-10 pcu-color-primary' : 'pcu-bg-gray-100 pcu-color-gray-600'}`}>
                                                        {item.type}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="pcu-padding-y-80 pcu-bg-white pcu-radius-24 pcu-border-light pcu-text-center">
                                        <Empty
                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                            description={
                                                <div className="pcu-flex-column pcu-gap-6">
                                                    <Text className="pcu-font-16 pcu-font-700 pcu-color-gray-800">No point history yet</Text>
                                                    <Text className="pcu-font-13 pcu-color-gray-500">Earn points on every purchase to see them here.</Text>
                                                </div>
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

        </div>
    );
};

export default BuyerRewardPoints;
